import { useCallback, type RefObject } from 'react';
import { useIntl } from 'react-intl';

import type {
	ExtensionConfiguration,
	BlockMenuExtensionConfiguration,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension/types';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension/selection-extension-plugin-type';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import { expVal } from '@confluence/feature-experiments';
import { useIssueCreateSidePanelActions } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanel.ts';
import { BULK_ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import type { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { convertSelectionToLegacyFormat } from '@confluence/highlight-actions/entry-points/convertSelectionToLegacyFormat.ts';
import {
	prepareBulkCreateFromSelection,
	BulkSelectionResult,
} from '@confluence/issue-create-side-panel/entry-points/bulkCreateSelectionUtils.ts';
import {
	useSetIsCreatedFromTable,
	useSetIsCreatedFromActionItems,
	useGetContentLastModifiedTimestamp,
	useSetSelectedTableAdf,
} from '@confluence/issue-create-side-panel/entry-points/BulkCreateContextProvider.ts';
import { getClosestActionListFromSelection } from '@confluence/issue-create-side-panel/entry-points/actionItemUtils.ts';

import { messages } from '../messages.ts';
import {
	createBulkJiraWorkItemsHandle,
	createSingleJiraWorkItemHandle,
} from '../jiraWorkCreateHandlers.ts';
import { isSelectionForBulkCreate } from '../utils/selectionUtils.ts';
export type UseJiraWorkCreateSelectionExtensionV2Props = {
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

const WorkItemCreationType = {
	SINGLE_WORK_ITEM: 'SINGLE_ISSUE',
	MULTIPLE_WORK_ITEM: 'MULTIPLE_ISSUE',
	AI_WORK_ITEM: 'AI_ISSUE',
} as const;

type WorkItemCreationType = (typeof WorkItemCreationType)[keyof typeof WorkItemCreationType];

export const useJiraWorkCreateSelectionExtensionV2 = ({
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
	editorAPIRef,
}: UseJiraWorkCreateSelectionExtensionV2Props): ExtensionConfiguration[] => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const actions = useIssueCreateSidePanelActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const setIsCreatedFromTable = useSetIsCreatedFromTable();
	const setIsCreatedFromActionItems = useSetIsCreatedFromActionItems();
	const lastModified = useGetContentLastModifiedTimestamp();
	const setSelectedTableAdf = useSetSelectedTableAdf();
	const fireCreateWorkItemButtonClickedAnalytics = useCallback(
		(
			workItemCreationType: WorkItemCreationType,
			isCreatedFromTable: boolean,
			isCreatedFromActionItems: boolean,
		) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'jiraIssueLink',
					type: 'ui',
					attributes: {
						issueCreationType: workItemCreationType,
						isDropdownItem: false,
						isCreatedFromTable,
						isCreatedFromActionItems,
						pageMode: pageModeRef.current,
						hasEditPermission: hasEditPermissionRef.current,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, pageModeRef, hasEditPermissionRef],
	);

	const initializeCreateState = useCallback(() => {
		actions.setHasEditPermission(hasEditPermissionRef.current);
		const activeSelection = window.getSelection();
		const selectionAdfResult =
			editorAPIRef.current?.selectionExtension?.actions?.getSelectionAdf?.();
		const container = document.querySelector<HTMLElement>('div#ak-editor-textarea');
		return { activeSelection, selectionAdfResult, container };
	}, [actions, hasEditPermissionRef, editorAPIRef]);

	const handleSingleCreateClick = useCallback(() => {
		const { activeSelection, selectionAdfResult, container } = initializeCreateState();

		const actionItemSelectionIfPresent = getClosestActionListFromSelection(
			activeSelection,
			pageModeRef.current,
		);
		fireCreateWorkItemButtonClickedAnalytics(
			WorkItemCreationType.SINGLE_WORK_ITEM,
			false,
			!!actionItemSelectionIfPresent,
		);

		createSingleJiraWorkItemHandle({
			activeSelection,
			container,
			contentId,
			contentType,
			rightSidebarContext: rightSidebarContextRef?.current,
			actions,
			isActionItemInSelection: !!actionItemSelectionIfPresent,
			pageMode: pageModeRef.current,
			editorAPI: editorAPIRef.current,
			selectionAdfResult,
		});
	}, [
		initializeCreateState,
		contentId,
		actions,
		pageModeRef,
		rightSidebarContextRef,
		fireCreateWorkItemButtonClickedAnalytics,
		contentType,
		editorAPIRef,
	]);

	const handleBulkCreateClick = useCallback(() => {
		const { activeSelection, selectionAdfResult, container } = initializeCreateState();

		if (!contentId) {
			//We'd always expect a contentId to be returned from the page context.
			//In the unexpected case where it is not, the button press will be a no-op and the user can try clicking again,
			//upon which the contentId should hopefully be populated.
			return;
		}

		const result = prepareBulkCreateFromSelection({
			actions,
			activeSelection,
			contentId,
			container,
			lastModified,
			pageMode: pageModeRef.current,
			hasEditPermission: hasEditPermissionRef.current,
			setIsCreatedFromTable,
			setIsCreatedFromActionItems,
			convertSelectionToLegacyFormat,
			selectionAdfResult,
		});

		if (result === BulkSelectionResult.Table) {
			const tableAdf = selectionAdfResult?.selectedNodeAdf;
			setSelectedTableAdf(tableAdf ?? undefined);
		}

		if (result === BulkSelectionResult.None) {
			//In the unexpected case where the selection cannot be prepared for bulk create, we fall back to the single create experience
			void handleSingleCreateClick();
			return;
		}

		fireCreateWorkItemButtonClickedAnalytics(
			WorkItemCreationType.MULTIPLE_WORK_ITEM,
			result === BulkSelectionResult.Table,
			result === BulkSelectionResult.ActionItems,
		);

		createBulkJiraWorkItemsHandle({
			rightSidebarContext: rightSidebarContextRef?.current,
			actions,
			editorAPI: editorAPIRef.current,
		});
	}, [
		initializeCreateState,
		actions,
		contentId,
		lastModified,
		pageModeRef,
		hasEditPermissionRef,
		rightSidebarContextRef,
		setIsCreatedFromTable,
		setIsCreatedFromActionItems,
		setSelectedTableAdf,
		editorAPIRef,
		handleSingleCreateClick,
		fireCreateWorkItemButtonClickedAnalytics,
	]);

	if (!expVal('confluence-bulk-work-create-editor', 'isEnabled', false)) {
		return [];
	}

	const getMenuItemsFactory = (keyPrefix: string) => () => {
		const currentSidebar = rightSidebarContextRef?.current?.getCurrent();
		const isDisabled =
			currentSidebar?.panelId === ISSUE_CREATE_SIDE_PANEL_ID ||
			currentSidebar?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

		const makeBulkCreateMenuItem = () => ({
			key: `${keyPrefix}-bulk-create`,
			label: formatMessage(messages.bulkCreateWorkItemNameUpdated),
			icon: WorkItemsIcon,
			onClick: () => handleBulkCreateClick(),
			isDisabled,
		});

		const makeSingleCreateMenuItem = () => ({
			key: `${keyPrefix}-single-create`,
			label: formatMessage(messages.singleCreateWorkItemNameUpdated),
			icon: WorkItemIcon,
			onClick: () => handleSingleCreateClick(),
			isDisabled,
		});

		return isSelectionForBulkCreate(editorAPIRef?.current)
			? [makeBulkCreateMenuItem()]
			: [makeSingleCreateMenuItem()];
	};

	const blockMenuConfiguration: BlockMenuExtensionConfiguration = {
		getMenuItems: getMenuItemsFactory('jira-work-create-block-menu'),
	};

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getMenuItems: getMenuItemsFactory('jira-work-create-toolbar'),
	};

	return [
		{
			key: 'jira-work-create',
			source: 'first-party',
			inlineToolbar: toolbarConfiguration,
			blockMenu: blockMenuConfiguration,
		},
	];
};
